import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const PlantsContext = createContext(null);
const API_URL = process.env.REACT_APP_BACKEND_URL;

/** Provides the logged-in tenant's configured plant list (["P1","P2","P3"…]).
 * Falls back to ["P1","P2"] for single-plant / legacy tenants. */
export function PlantsProvider({ children }) {
  const { user } = useAuth();
  const [plants, setPlants] = useState(['P1', 'P2']);
  const [hasMultiple, setHasMultiple] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const applySettings = useCallback(data => {
    setPlants(Array.isArray(data.plants) && data.plants.length ? data.plants : ['P1', 'P2']);
    setHasMultiple(!!data.has_multiple_plants);
    setLoaded(true);
  }, []);

  const refresh = useCallback(async () => {
    if (!user || user === false || user.role === 'master') { setLoaded(true); return; }
    try {
      const { data } = await axios.get(`${API_URL}/api/company/settings`, { withCredentials: true });
      setPlants(Array.isArray(data.plants) && data.plants.length ? data.plants : ['P1', 'P2']);
      setHasMultiple(!!data.has_multiple_plants);
    } catch { /* silent — stays on fallback */ }
    finally { setLoaded(true); }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  const save = useCallback(async ({ has_multiple_plants, plants: plantList }) => {
    const payload = { has_multiple_plants, plants: plantList };
    const { data } = await axios.put(`${API_URL}/api/company/settings`, payload, { withCredentials: true });
    setPlants(data.plants);
    setHasMultiple(data.has_multiple_plants);
    return data;
  }, []);

  return (
    <PlantsContext.Provider value={{ plants, hasMultiple, loaded, refresh, save, applySettings }}>
      {children}
    </PlantsContext.Provider>
  );
}

export function usePlants() {
  const ctx = useContext(PlantsContext);
  // Graceful fallback — if a page is rendered outside the provider (e.g. login page) just return defaults
  if (!ctx) return { plants: ['P1', 'P2'], hasMultiple: false, loaded: true, refresh: () => {}, save: async () => {}, applySettings: () => {} };
  return ctx;
}
